import { Economy, LevelCatalog, petIndex, upgradeTargetName } from "@highmarch/app";
import { BalanceData, PlayerProfile, UpgradeJob, UpgradeTarget, cancelQueued, queueUpgrade, troopStatMult } from "@highmarch/domain";
import { audio } from "../../audio/audio";
import { fmtDuration } from "../format";
import { icon } from "../iconsKit";
import { barHtml } from "../kit";
import { portraitImg } from "../portraits";
import { describeCost } from "../gearUi";
import { gainPercent } from "../upgradePreview";
import { levelGainText } from "./levelGain";
import { guide } from "../guide/guide";
import { toast } from "../toast";
import { economyLink } from "./economyLink";
import { econCue } from "./purchaseFx";
import { refusalWords } from "./refusalWords";
import { UpgradeAction, queueRows, startsNow, upgradeAction } from "./queueModel";
import { archetypeStats, roleLine } from "../towerFacts";
import { sheetHeadHtml } from "../components";
import { openResearch, researchRowHtml } from "../research/researchSheet";
import { finishNowKey, finishNowOffer, finishNowPrimaryHtml, finishNowSecondaryHtml, finishNowTap } from "./finishNowUi";
import { PetSheetPart, petSheetPart } from "../pets/petSheetPart";
import { goldPillHtml, nextRowHtml, statChipsHtml, upgradeBtnHtml } from "./upgradeBits";
import { notEnoughGold, queueGoldNeeded } from "./getGold";

/** A queued upgrade on this target: paid, waiting for a builder, cancellable for everything back. */
function queuedHtml(id: number, toLevel: number, startsIn: number): string {
  return `<div class="qd">${icon("clock", 16)}<div class="qt"><b>Queued: Lv ${toLevel}</b><small>${startsIn > 0 ? `Starts in ${fmtDuration(startsIn)}` : "Starts next"}</small></div><button class="btn dark qcancel" data-id="${id}">CANCEL</button></div>`;
}

/**
 * The next level and the one button for it (queueModel.ts `upgradeAction`): UPGRADE when a builder is free, QUEUE NEXT
 * when all are busy (or this target already has a job), else why not. `secondary`: a job or a queued level is shown
 * above, so this is the level after it. The timer is the profile's (VIP build speed included, docs/SHOP.md §8.3).
 */
function nextHtml(B: BalanceData, p: PlayerProfile, towerIndex: number, a: UpgradeAction, secondary: boolean): string {
  if (a.kind === "max") return secondary ? "" : `<div class="next us-next us-max"><b class="disp">MAX LEVEL</b></div>`;
  // what the level adds against the one before it, in the stats this tower uses (levelGain.ts: no damage on a Frost)
  const row = nextRowHtml(a.toLevel - 1, a.toLevel, levelGainText(B, towerIndex, a.toLevel), a.kind === "queueFull" ? 0 : p.durationFor(a.toLevel), secondary);
  if (a.kind === "queueFull") return `${row}${secondary ? "" : `<button class="btn dark disp" disabled>NO FREE BUILDER</button>`}`;
  return `${row}${upgradeBtnHtml(a.kind === "upgrade" ? "upgrade" : "queue", a.cost, a.affordable)}`;
}

/**
 * The fortress's level makes its troops tougher (DESIGN §7): what they have now and what the next level adds, so
 * powering up the base visibly powers up its soldiers too (owner 2026-09-30).
 */
function troopsHtml(B: BalanceData, level: number): string {
  const R = B.economy.troops, now = troopStatMult(R, level).toNumber();
  return `Troops ${gainPercent(now)}`;
}

/** The VIP free finish (docs/SHOP.md §2; @highmarch/app Deals): a running upgrade with `freeFinishMinutes` or less left ends now. */
export interface FreeFinish {
  canFinishFree(jobId: number, now: number): true | string;
  /** Finishes it and returns the jobs that completed (claimed). */
  finishFree(jobId: number, now: number): UpgradeJob[] | string;
}

export interface UpgradeSheetContext {
  balance: BalanceData; profile: PlayerProfile; now: () => number; onChange: () => void;
  /** VIP's free finish (the catalog's Deals); absent: no FREE button. */
  freeFinish?: FreeFinish;
  /** Jobs this sheet completed (a free finish): the hub celebrates them as it does a timer that ran out. */
  onJobsDone?(done: UpgradeJob[]): void;
  /** The catalog, for the pet sheet (Pets v2: its come-along switch and coats); absent: no pet sheet. */
  catalog?: LevelCatalog;
}

/** What the open sheet shows: a tower or the Fortress (`index` its tower index), or a pet (its roster index). */
interface Shown { readonly target: UpgradeTarget; readonly index: number }

/**
 * Tower / base upgrade sheet (parchment): level, next-level cost and time, running job with bar, speed-up and cancel.
 * Pets v2: the pet sheet is this sheet too (`openPet`), so a pet's upgrade takes the same builders, queue, speed-ups,
 * VIP finish and FINISH NOW as a tower's; only its head and body are the pet's (ui/pets/petSheetPart.ts).
 */
export class UpgradeSheet {
  private el: HTMLElement | null = null;
  private shown: Shown = { target: UpgradeTarget.Tower, index: -1 };
  /** Whether the open sheet shows the FREE finish: the tick re-opens it when the job crosses the VIP threshold. */
  private freeShown = false;
  /** The FINISH NOW the open sheet shows (finishNowKey): the tick re-opens it when the price or the wallet moves. */
  private finishShown = "-";

  constructor(private readonly parent: HTMLElement, private readonly ctx: UpgradeSheetContext) {}

  /** `isConnected`: the hub's sheet guard (sheetGuard.ts) removes this sheet when another one opens — a stale one must not re-open itself. */
  get isOpen(): boolean { return this.el?.isConnected ?? false; }

  open(towerIndex: number): void {
    const t = this.ctx.balance.towers[towerIndex];
    if (t) this.show({ target: t.isBase ? UpgradeTarget.Base : UpgradeTarget.Tower, index: towerIndex });
  }

  /** The pet sheet (Pets v2) for pet `petId`, if the catalog is wired and the pet is caught. */
  openPet(petId: string): void {
    const i = petIndex(this.ctx.balance, petId);
    if (i >= 0 && this.ctx.catalog && this.ctx.profile.levelOf(UpgradeTarget.Pet, i) > 0) this.show({ target: UpgradeTarget.Pet, index: i });
  }

  private reopen(): void { this.show(this.shown); }

  private show(shown: Shown): void {
    this.close();
    this.shown = shown;
    const { balance: B, profile: p } = this.ctx;
    const { target, index } = shown, isBase = target === UpgradeTarget.Base, isPet = target === UpgradeTarget.Pet;
    const level = p.levelOf(target, index);
    const job = p.jobs.find((j) => j.target === target && (isBase || j.towerIndex === index));
    const now = this.ctx.now();
    const action = upgradeAction(p, target, index);
    const onTarget = queueRows(B, p, now).filter((q) => q.target === target && (isBase || q.towerIndex === index));
    const free = job !== undefined && this.canFree(job.id, now);
    this.freeShown = free;
    // MP-07 (a): FINISH NOW for gems — the primary with no speed-up in hand, else the secondary under the speed-up.
    const fin = job !== undefined && !free ? finishNowOffer(job.id, now) : null;
    this.finishShown = finishNowKey(fin);
    // Under the VIP threshold the FREE finish takes the speed-up's place (a speed-up would only waste its minutes).
    const first = free ? `<button class="btn disp freefinish">${icon("star", 16)} FREE FINISH<small>VIP</small></button>`
      : p.speedUps.length > 0 ? `<button class="btn gold speedup">${icon("bolt", 16)} SPEED-UP ${fmtDuration(Math.max(...p.speedUps) * 60)}</button>`
      : fin ? finishNowPrimaryHtml(fin) : `<button class="btn dark" disabled>NO SPEED-UPS</button>`;
    const second = fin && p.speedUps.length > 0 ? finishNowSecondaryHtml(fin) : "";
    const secondary = job !== undefined || onTarget.length > 0;
    const part = isPet ? petSheetPart(this.ctx.catalog!, index, action, secondary, this.queueHint(), () => { this.ctx.onChange(); this.reopen(); }) : this.towerPart(index, level, action, secondary);
    const sheet = document.createElement("div");
    sheet.className = `sheet panel upgrade${isPet ? " petsheet" : ""}`;
    sheet.innerHTML = `
      ${part.head}${part.body}
      ${job ? `<div class="job"><b>Upgrading to Lv ${job.toLevel}</b>${barHtml(this.progress(job) / 100, "gold")}<small class="eta" data-job="${job.id}">${icon("clock", 13)} ${fmtDuration(job.endAt - now)}</small>
             <div class="jobactions">${first}<button class="btn dark cancel">CANCEL<small>${Math.round(B.meta.cancelRefundFraction.toNumber() * 100)}% back</small></button></div>${second}</div>` : ""}
      ${onTarget.map((q) => queuedHtml(q.id, q.toLevel, q.startsIn)).join("")}
      ${part.next}${part.foot ?? ""}`;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => this.close();
    part.bind?.(sheet);
    if (startsNow(action)) sheet.querySelector(".us-go.upgrade")?.classList.add("nudge");
    this.bindJob(sheet, job, target, index, isPet ? upgradeTargetName(B, target, index) : isBase ? "Fortress" : B.towers[index].name);
    this.parent.append(sheet);
    this.el = sheet;
  }

  /** A tower's (or the Fortress's) head, stats, Research row and next level. */
  private towerPart(towerIndex: number, level: number, action: UpgradeAction, secondary: boolean): PetSheetPart {
    const B = this.ctx.balance, p = this.ctx.profile, t = B.towers[towerIndex], isBase = t.isBase;
    const mult = B.tables.levelMultAt(level).toNumber();
    return {
      head: sheetHeadHtml({ title: t.name, sub: level >= B.tables.maxLevel ? "Max level" : `Level ${level}`, portrait: portraitImg(t.id, 60, icon("tower", 34, "#fff8e6")), tint: "blue", extra: goldPillHtml(p.gold) }),
      body: `${statChipsHtml(archetypeStats(t, mult), isBase ? [troopsHtml(B, level)] : [])}${roleLine(t) ? `<div class="stats role">${roleLine(t)}</div>` : ""}${researchRowHtml(p, towerIndex)}`,
      next: nextHtml(B, p, towerIndex, action, secondary),
      // Gold Research (M35): this tower's (or the Fortress's) tree, in its own sheet.
      bind: (sheet) => sheet.querySelector<HTMLButtonElement>(".researchbtn")?.addEventListener("click", () => { this.close(); openResearch(this.parent, t.id); }),
    };
  }

  /** The job's and the next level's buttons, the same for every target. */
  private bindJob(sheet: HTMLElement, job: UpgradeJob | undefined, target: UpgradeTarget, index: number, name: string): void {
    const p = this.ctx.profile;
    sheet.querySelector<HTMLButtonElement>(".speedup")?.addEventListener("click", () => {
      if (!job) return;
      const idx = p.speedUps.indexOf(Math.max(...p.speedUps)); // biggest first: fewest taps to finish a long timer
      const res = p.applySpeedUp(idx, job.id, this.ctx.now());
      if (typeof res === "string") return;
      this.ctx.onChange(); this.reopen();
    });
    sheet.querySelector<HTMLButtonElement>(".freefinish")?.addEventListener("click", () => {
      if (!job || !this.ctx.freeFinish) return;
      const done = this.ctx.freeFinish.finishFree(job.id, this.ctx.now());
      if (typeof done === "string") { this.reopen(); return; }
      if (this.ctx.onJobsDone) this.ctx.onJobsDone(done);   // the hub saves, celebrates and refreshes this sheet
      else { this.ctx.onChange(); this.reopen(); }
    });
    sheet.querySelector<HTMLButtonElement>(".finishnow")?.addEventListener("click", () => void this.finishNow(job));
    sheet.querySelector<HTMLButtonElement>(".cancel")?.addEventListener("click", () => { if (!job) return; p.cancelUpgrade(job.id); this.ctx.onChange(); this.reopen(); });
    sheet.querySelector<HTMLButtonElement>(".upgrade")?.addEventListener("click", () => {
      const res = p.startUpgrade(target, index, this.ctx.now());
      if (res === "not enough gold") { void notEnoughGold(this.parent, p.costFor(p.levelOf(target, index) + 1) - p.gold, sheet); return; }
      if (typeof res === "string") { toast(this.parent, refusalWords(res)); return; }
      audio.play("hubUpgradeStart");                    // the builder takes the job; the finish plays from HubView.tick
      guide()?.signal("upgradeStarted");
      this.ctx.onChange(); this.reopen();
    });
    sheet.querySelector<HTMLButtonElement>(".queuego")?.addEventListener("click", () => {
      const q = queueUpgrade(p, target, index, this.ctx.now());
      if (q === "not enough gold") { void notEnoughGold(this.parent, queueGoldNeeded(p, target, index), sheet); return; }
      if (typeof q === "string") { toast(this.parent, refusalWords(q)); return; }
      econCue("queue");
      toast(this.parent, `Queued: ${name} → Lv ${q.toLevel}. It starts when a builder is free.`);
      this.ctx.onChange(); this.reopen();
    });
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".qcancel")) b.onclick = () => {
      if (!cancelQueued(p, Number(b.dataset.id))) return;
      toast(this.parent, "Queued upgrade cancelled: everything paid back");
      this.ctx.onChange(); this.reopen();
    };
  }

  /** "Hire another builder in the Shop" under a full queue — left out in the quiet after a loss (no nudges then). */
  private queueHint(): string {
    const link = economyLink();
    const eco = link ? new Economy(link.catalog) : null;
    return eco && eco.nextBuilderPrice() !== null && eco.offersAllowed("hub", this.ctx.now()) ? "Hire another builder in the Shop for one more slot." : "";
  }

  /** Every second: move the bar and the ETA without rebuilding. */
  tick(): void {
    if (!this.isOpen) return;
    const el = this.el?.querySelector<HTMLElement>(".job .eta");
    const fill = this.el?.querySelector<HTMLElement>(".job .fill");
    if (!el) return;
    const job = this.ctx.profile.jobs.find((j) => j.id === Number(el.dataset.job));
    const now = this.ctx.now();
    if (!job || this.canFree(job.id, now) !== this.freeShown || (!this.freeShown && finishNowKey(finishNowOffer(job.id, now)) !== this.finishShown)) { this.reopen(); return; }
    el.innerHTML = `${icon("clock", 13)} ${fmtDuration(job.endAt - this.ctx.now())}`;
    if (fill) fill.style.width = `${this.progress(job)}%`;
  }

  /** Re-open on the same tower (or pet) after the profile changed (a job finished). */
  refresh(): void { if (this.isOpen) this.reopen(); }

  close(): void { this.el?.remove(); this.el = null; }

  /** FINISH NOW tapped: confirm, pay, and the hub celebrates the finished job like any other (or the sheet redraws). */
  private async finishNow(job: UpgradeJob | undefined): Promise<void> {
    if (!job) return;
    const done = await finishNowTap(this.parent, this.ctx.balance, job, this.ctx.now);
    if (!done) { if (this.isOpen) this.reopen(); return; }
    if (this.ctx.onJobsDone) this.ctx.onJobsDone(done);
    else { this.ctx.onChange(); this.reopen(); }
  }

  private canFree(jobId: number, now: number): boolean { return this.ctx.freeFinish?.canFinishFree(jobId, now) === true; }

  private progress(job: UpgradeJob): number {
    return Math.min(100, Math.max(0, ((this.ctx.now() - job.startedAt) / Math.max(1, job.endAt - job.startedAt)) * 100));
  }
}
